import { Setting } from '@/common/model/setting.model';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { MstDeviceType } from '@/common/enum/mst-device-type.enum';
import AppConstants from '@/common/constants/app.constants';
import { ChuteModeType } from '@/tsort/enum/chute-mode-type.enum';
import { UserLastAccessInfo } from '@/common/model/user-last-access-info.model';
export class SettingStoreConst {
  public static readonly SETTING_STORE_MODULE_NAME = 'SettingStore';
}

const SETTING_STORAGE_NAME = 'setting';
@Module({ namespaced: true })
export default class SettingStore extends VuexModule {
  _settingStorage: string | null = localStorage.getItem(SETTING_STORAGE_NAME);

  _setting: Setting = this._settingStorage
    ? JSON.parse(this._settingStorage)
    : {
        deviceId: '',
        externalDeviceId: '',
        inductionId: '',
        inductionName: '',
        deviceType: MstDeviceType.HT,
        chuteMode: 0,
        rcsName: '',
        bluetoothAddress: '',
        residueSku: 0,
        userId: 0,
        restoreScreen: [],
        lastScreenUrl: '/top',
        isInductionBreaking: false
      };

  @Mutation
  public setDeviceId(deviceId: string) {
    this._setting.deviceId = deviceId;
  }

  @Mutation
  public setChuteMode(_type: ChuteModeType) {
    let type = null;
    if (typeof type === 'string') {
      type = Number.parseInt(type) as number;
    } else {
      type = _type;
    }
    // 設定する前に数値に変換
    this._setting.chuteMode = type;
  }

  @Mutation
  public setDeviceType(_type: MstDeviceType | string) {
    let type = null;
    if (typeof type === 'string') {
      type = Number.parseInt(type) as MstDeviceType;
    } else {
      type = _type;
    }
    // 設定する前に数値に変換
    this._setting.deviceType = '' + type;
  }

  @Mutation
  public setExternalDeviceId(externalDeviceId: string) {
    this._setting.externalDeviceId = externalDeviceId;
  }

  @Mutation
  public setInductionId(inductionId: string) {
    this._setting.inductionId = inductionId;
  }

  @Mutation
  public setInductionName(inductionName: string) {
    this._setting.inductionName = inductionName;
  }

  @Mutation
  public setRcsName(rcsName: string) {
    this._setting.rcsName = rcsName;
  }

  @Mutation
  public setBluetoothAddress(bluetoothAddress: string) {
    this._setting.bluetoothAddress = bluetoothAddress;
  }

  @Mutation
  public setUserId(userId: number) {
    this._setting.userId = userId;
  }

  @Mutation
  public setRestoreScreen(restoreScreen: UserLastAccessInfo) {
    if (!this._setting.restoreScreen) {
      this._setting = { ...this._setting, userId: restoreScreen?.userId, restoreScreen: [] };
    }
    for (let idx = 0; idx < this._setting.restoreScreen.length; idx++) {
      const elm = this._setting.restoreScreen[idx];
      if (
        elm &&
        restoreScreen &&
        elm.userId === restoreScreen.userId &&
        elm.inductionId === restoreScreen.inductionId &&
        elm.externalDeviceId === restoreScreen.externalDeviceId
      ) {
        elm.lastAccessedScreen = restoreScreen.lastAccessedScreen;
        return;
      }
    }
    (this._setting.restoreScreen as Array<UserLastAccessInfo>)?.push(restoreScreen);
  }

  @Mutation
  public setLastScreenUrl(lastScreenUrl: string) {
    this._setting.lastScreenUrl = lastScreenUrl;
  }

  @Mutation
  public updateStorage(setting: Setting) {
    localStorage.setItem(SETTING_STORAGE_NAME, JSON.stringify(setting));
  }

  @Mutation
  public setInductionBreakingFlag(isInductionBreaking: boolean) {
    this._setting.isInductionBreaking = isInductionBreaking;
  }

  @Action({ rawError: true })
  public updateChuteMode(value: number): void {
    this.context.commit('setChuteMode', value);
    this.context.commit('updateStorage', this._setting);
  }

  @Action({ rawError: true })
  public updateDeviceId(value: string): void {
    this.context.commit('setDeviceId', value);
    this.context.commit('updateStorage', this._setting);
  }

  @Action({ rawError: true })
  public updateDeviceType(value: string): void {
    this.context.commit('setDeviceType', value);
    this.context.commit('updateStorage', this._setting);
  }

  @Action({ rawError: true })
  public updateExternalDeviceId(value: string): void {
    this.context.commit('setExternalDeviceId', value);
    this.context.commit('updateStorage', this._setting);
  }

  @Action({ rawError: true })
  public updateInductionId(value: string): void {
    this.context.commit('setInductionId', value);
    this.context.commit('updateStorage', this._setting);
  }

  @Action({ rawError: true })
  public updateInductionName(value: string): void {
    this.context.commit('setInductionName', value);
    this.context.commit('updateStorage', this._setting);
  }

  @Action({ rawError: true })
  public updateRcsName(value: string): void {
    this.context.commit('setRcsName', value);
    this.context.commit('updateStorage', this._setting);
  }

  @Action({ rawError: true })
  public updateBluetoothAddress(value: string): void {
    this.context.commit('setBluetoothAddress', value);
    this.context.commit('updateStorage', this._setting);
  }

  @Action({ rawError: true })
  public updateDeviceIdAndInductionId(deviceId: string, inductionId: string): void {
    this.context.commit('setDeviceId', deviceId);
    this.context.commit('setInductionId', inductionId);
    this.context.commit('updateStorage', this._setting);
  }

  @Action({ rawError: true })
  public updateUserId(userId: number): void {
    this.context.commit('setUserId', userId);
    this.context.commit('updateStorage', this._setting);
  }

  @Action({ rawError: true })
  public updateRestoreScreen(restoreScreen: UserLastAccessInfo): void {
    this.context.commit('setRestoreScreen', restoreScreen);
    this.context.commit('updateStorage', this._setting);
  }

  @Action({ rawError: true })
  public updateLastScreenUrl(value: string): void {
    this.context.commit('setLastScreenUrl', value);
    this.context.commit('updateStorage', this._setting);
  }

  @Action({ rawError: true })
  public updateInductionBreakingFlag(value: boolean): void {
    this.context.commit('setInductionBreakingFlag', value);
    this.context.commit('updateStorage', this._setting);
  }

  get chuteMode(): ChuteModeType | undefined {
    return this._setting.chuteMode;
  }

  get deviceId(): string | undefined {
    return this._setting.deviceId;
  }

  get externalDeviceId(): string | undefined {
    return this._setting.externalDeviceId;
  }

  get inductionId(): string | undefined {
    return this._setting.inductionId;
  }

  get inductionName(): string | undefined {
    return this._setting.inductionName;
  }

  get deviceType(): MstDeviceType | null {
    const strType = this._setting.deviceType || `${AppConstants.DEVICE_TYPE}`;
    const type = Number.parseInt(strType) as MstDeviceType;
    return type;
  }

  get bluetoothAddress(): string | undefined {
    return this._setting.bluetoothAddress;
  }

  get rcsName(): string | undefined {
    return this._setting.rcsName;
  }

  get restoreScreen(): string {
    for (let idx = 0; idx < this._setting.restoreScreen?.length; idx++) {
      const elm = this._setting.restoreScreen[idx];
      if (
        elm &&
        elm.inductionId === this.inductionId &&
        elm.externalDeviceId === this.externalDeviceId &&
        elm.userId === this._setting.userId
      ) {
        return elm.lastAccessedScreen;
      }
    }
    return AppConstants.NAME_TOP_PAGE;
  }

  get lastScreenUrl(): string | undefined {
    return this._setting.lastScreenUrl;
  }

  get isInductionBreaking(): boolean {
    return this._setting.isInductionBreaking;
  }
}
